/**
 * Two-layer exchange-rate cache: in-memory (fastest, per tab) backed by
 * localStorage (survives reloads, shared across tabs).
 *
 * Entry shape:
 *   { base: 'USD', rates: { PKR: 277.9, ... }, fetchedAt: ms, expiresAt: ms, provider: string }
 */
const STORAGE_KEY = 'cd_exchange_rates_v1'

export const RATES_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

let memoryCache = null

function isValidEntry(entry) {
  return Boolean(
    entry
    && entry.base === 'USD'
    && entry.rates
    && typeof entry.rates === 'object'
    && typeof entry.expiresAt === 'number',
  )
}

export function readCache() {
  if (isValidEntry(memoryCache)) return memoryCache
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!isValidEntry(parsed)) return null
    memoryCache = parsed
    return parsed
  } catch (e) {
    // JSON corruption or storage disabled — treat as cache miss
    return null
  }
}

export function writeCache({ rates, expiresAt, provider }) {
  const entry = {
    base: 'USD',
    rates,
    fetchedAt: Date.now(),
    expiresAt,
    provider,
  }
  memoryCache = entry
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  } catch (e) {
    // quota exceeded / private mode — in-memory cache still works for this session
  }
  return entry
}

export function isFresh(entry) {
  return isValidEntry(entry) && entry.expiresAt > Date.now()
}

export function clearCache() {
  memoryCache = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}
